import { type ComponentProps,useCallback, useMemo } from "react";

import type { RuleVariable } from "../hooks/useRuleVariables";
import { useLoopNavigation } from "../nodes/loop/loopContext";
import { getActiveCanvasScopeNodes, getLoopNodeData, type LoopStackEntry } from "../nodes/loop/loopTypes";
import type { RuleNode } from "../types/nodes";
import { resolveIteratedDatabaseMultiFromLoopNodeData } from "../utils/loop-resource-variables";
import Toolbox from "./Toolbox";

type ToolboxProps = ComponentProps<typeof Toolbox>;

export type CanvasScopedToolboxProps = ToolboxProps & {
	nodes: RuleNode[];
};

function reactionVariablesFromScopeNodes(scopeNodes: RuleNode[]): RuleVariable[] {
	const out: RuleVariable[] = [];
	for (const n of scopeNodes) {
		if (n.data.type !== "reaction") continue;
		const name = n.data.name;
		if (!name || typeof name !== "string") continue;
		out.push({
			id: `reaction-${n.id}`,
			name: name.trim() || name,
			type: "reaction",
			sourceNodeId: n.id,
		});
	}
	return out;
}

function resolveLoopIteratedDatabaseMultiVariable(
	nodes: RuleNode[],
	loopStack: LoopStackEntry[],
	databaseMultiRowFromProps: RuleVariable[],
	fullVariableUniverse: RuleVariable[] | undefined,
): RuleVariable | null {
	if (loopStack.length === 0) return null;
	const resolved = getLoopNodeData(nodes, loopStack);
	if (!resolved) return null;
	const pool =
		fullVariableUniverse?.filter((v) => v.type === "databaseMulti") ?? databaseMultiRowFromProps;
	return resolveIteratedDatabaseMultiFromLoopNodeData(resolved.loopNodeData, pool);
}

/**
 * Multi-row DB + reactions: scoped to the active canvas. Single-row DB vars stay rule-wide in
 * loop sub-canvas so root (and unscoped) sources stay usable. Reactions are also synthesized from
 * scope nodes so the list stays correct if variable state is briefly stale.
 */
const CanvasScopedToolbox = ({ nodes, ...toolboxProps }: CanvasScopedToolboxProps) => {
	const { loopStack } = useLoopNavigation();

	const scopeNodeIds = useMemo(() => {
		const scopeNodes = getActiveCanvasScopeNodes(nodes, loopStack);
		return new Set(scopeNodes.map((n) => n.id));
	}, [nodes, loopStack]);

	const isRootCanvas = loopStack.length === 0;

	const filterGraphBacked = useCallback(
		(list: RuleVariable[]) =>
			list.filter((v) => {
				if (!v.sourceNodeId) {
					return isRootCanvas;
				}
				return scopeNodeIds.has(v.sourceNodeId);
			}),
		[isRootCanvas, scopeNodeIds],
	);

	const databaseSingleRowVariables = useMemo(
		() =>
			isRootCanvas
				? filterGraphBacked(toolboxProps.databaseSingleRowVariables)
				: toolboxProps.databaseSingleRowVariables,
		[filterGraphBacked, isRootCanvas, toolboxProps.databaseSingleRowVariables],
	);

	const databaseMultiRowVariables = useMemo(
		() => filterGraphBacked(toolboxProps.databaseMultiRowVariables),
		[filterGraphBacked, toolboxProps.databaseMultiRowVariables],
	);

	const reactionVariables = useMemo(() => {
		const scopeNodes = getActiveCanvasScopeNodes(nodes, loopStack);
		const fromState = filterGraphBacked(toolboxProps.reactionVariables);
		const fromNodes = reactionVariablesFromScopeNodes(scopeNodes);
		const bySource = new Map<string, RuleVariable>();
		for (const v of fromNodes) {
			if (v.sourceNodeId) {
				bySource.set(v.sourceNodeId, v);
			}
		}
		for (const v of fromState) {
			if (v.sourceNodeId) {
				bySource.set(v.sourceNodeId, v);
			}
		}
		return [...bySource.values()];
	}, [
		nodes,
		loopStack,
		filterGraphBacked,
		toolboxProps.reactionVariables,
	]);

	const loopIteratedDatabaseMultiVariable = useMemo(
		() =>
			resolveLoopIteratedDatabaseMultiVariable(
				nodes,
				loopStack,
				toolboxProps.databaseMultiRowVariables,
				toolboxProps.fullVariableUniverse,
			),
		[
			nodes,
			loopStack,
			toolboxProps.databaseMultiRowVariables,
			toolboxProps.fullVariableUniverse,
		],
	);

	return (
		<Toolbox
			{...toolboxProps}
			databaseSingleRowVariables={databaseSingleRowVariables}
			databaseMultiRowVariables={databaseMultiRowVariables}
			reactionVariables={reactionVariables}
			showDatabaseMultiRowSection={isRootCanvas}
			inLoopCanvas={!isRootCanvas}
			loopIteratedDatabaseMultiVariable={loopIteratedDatabaseMultiVariable}
		/>
	);
};

export default CanvasScopedToolbox;
